import { Box, Button, Divider, Grid, styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { CollapsibleSection, Header, LwButton, LwFormInput, LwFormTime } from 'redesign';
import { useFormContext } from 'react-hook-form';
import { CompanyOrt } from '@types';
import { formatOrtPeriods, formatTimeToUi } from './utils';

const WEEKDAYS = ['Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag'];
const WEEKEND = ['Zaterdag', 'Zondag'];
const DAYS_OF_WEEK = [...WEEKDAYS, ...WEEKEND];

const DAY_NAME_MAP: { [key: string]: string } = {
  Maandag: 'monday',
  Dinsdag: 'tuesday',
  Woensdag: 'wednesday',
  Donderdag: 'thursday',
  Vrijdag: 'friday',
  Zaterdag: 'saturday',
  Zondag: 'sunday',
};

export type OrtPeriod = {
  startTime: string;
  endTime: string;
  percentage: string;
};

type OrtData = {
  [day: string]: OrtPeriod[];
};

type CompanyOrtFormProps = {
  onClose: () => void;
  ort: CompanyOrt | undefined;
  title: string;
};

type CompanyOrtFormData = {
  [key: string]: OrtPeriod[] | HolidayData;
  holidays: HolidayData;
};

type HolidayData = {
  [holidayName: string]: OrtPeriod[];
};

const getDayData = (ort: CompanyOrt | undefined, dayName: string) => {
  if (!ort?.calendar || typeof ort.calendar !== 'object') {
    return undefined;
  }
  return ort.calendar.workDays?.[dayName as keyof typeof ort.calendar.workDays];
};

function getInitialOrtDataForDays(
  ort: CompanyOrt | undefined,
  days: string[],
  dayNameMap: { [key: string]: string }
): OrtData {
  const initialData: OrtData = {};

  days.forEach((day) => {
    const apiDayName = dayNameMap[day];
    const dayData = getDayData(ort, apiDayName);

    if (dayData && dayData.length > 0) {
      initialData[day] = dayData.map((period: any) => ({
        startTime: formatTimeToUi(period.startTime),
        endTime: formatTimeToUi(period.endTime),
        percentage: period.percentage.toString(),
      }));
    } else {
      initialData[day] = [{ startTime: '00:00', endTime: '24:00', percentage: '' }];
    }
  });

  return initialData;
}

function getInitialOrtDataForHolidays(holidaysData: Record<string, any> | undefined): HolidayData {
  const initialHolidays: HolidayData = {};

  if (holidaysData && Object.keys(holidaysData).length > 0) {
    Object.entries(holidaysData).forEach(([name, periods]) => {
      if (Array.isArray(periods) && periods.length > 0) {
        initialHolidays[name] = periods.map((period: any) => ({
          startTime: formatTimeToUi(period.startTime),
          endTime: formatTimeToUi(period.endTime),
          percentage: period.percentage !== undefined ? String(period.percentage) : '',
        }));
      } else {
        initialHolidays[name] = [{ startTime: '00:00', endTime: '23:59', percentage: '' }];
      }
    });
  }

  return initialHolidays;
}

export const CompanyOrtForm = ({ onClose, ort, title }: CompanyOrtFormProps) => {
  const { control, getValues, setValue } = useFormContext<CompanyOrtFormData>();

  const [ortData, setOrtData] = useState<OrtData>(() =>
    getInitialOrtDataForDays(ort, DAYS_OF_WEEK, DAY_NAME_MAP)
  );

  const [holidays, setHolidays] = useState<HolidayData>(() =>
    getInitialOrtDataForHolidays(ort?.holidays)
  );

  useEffect(() => {
    if (ort) {
      DAYS_OF_WEEK.forEach((day) => {
        const periods = ortData[day] || [];
        setValue(day, periods);
      });

      if (Object.keys(holidays).length > 0) {
        setValue('holidays', holidays);
      } else {
        setValue('holidays', {});
      }
    }
  }, [ort, ortData, holidays, setValue]);

  const handleSave = () => {
    const ortValues = getValues();

    const completeOrtData: any = {
      workDays: {},
      holidays: {},
    };

    DAYS_OF_WEEK.forEach((day) => {
      const workDayOrts = ortValues[day] || [];
      completeOrtData.workDays[DAY_NAME_MAP[day]] = Array.isArray(workDayOrts)
        ? formatOrtPeriods(workDayOrts)
        : [];
    });

    const formHolidays = ortValues.holidays || {};
    Object.entries(formHolidays).forEach(([name, periods]: [string, any]) => {
      if (Array.isArray(periods)) {
        completeOrtData.holidays[name] = formatOrtPeriods(periods);
      }
    });

    onClose();
  };

  const holidayNames = Object.keys(holidays);

  return (
    <CompanyOrtFormWrapper>
      <Box mb={4}>
        <Header
          titleText={title}
          subheaderText={`Configureer het ORT-percentage voor verschillende tijdsperioden per dag.${
            ort ? ` (${ort.name})` : ''
          }`}
        />
        <Divider />
      </Box>
      <ContentBox>
        <CollapsibleSection title="Workdays" initializeAsOpen={true}>
          {DAYS_OF_WEEK.map((day) => {
            const periodCount = ortData[day].length;

            return (
              <Box key={day} mb={2}>
                <CollapsibleSection
                  title={`${day} (${periodCount} periode${periodCount !== 1 ? 's' : ''})`}
                  warningText={undefined}
                  initializeAsOpen={false}
                >
                  <Box p={2}>
                    <Grid container spacing={2}>
                      {ortData[day].map((period, index) => (
                        <Grid item xs={12} key={index}>
                          <PeriodCard>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={2}>
                                <LwFormTime
                                  name={`${day}.${index}.startTime`}
                                  label="Van"
                                  control={control}
                                  disabled={false}
                                />
                              </Grid>

                              <Grid item xs={2}>
                                <LwFormTime
                                  name={`${day}.${index}.endTime`}
                                  label="Tot"
                                  control={control}
                                  disabled={false}
                                />
                              </Grid>

                              <Grid item xs={3}>
                                <LwFormInput
                                  name={`${day}.${index}.percentage`}
                                  label="ORT %"
                                  control={control}
                                  inputType="number"
                                />
                              </Grid>
                            </Grid>
                          </PeriodCard>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </CollapsibleSection>
              </Box>
            );
          })}
        </CollapsibleSection>
        <Box mt={4} />
        <CollapsibleSection title={`Holidays (${holidayNames.length})`} initializeAsOpen={false}>
          {holidayNames.map((holidayName) => {
            const periodCount = holidays[holidayName].length;

            return (
              <Box key={holidayName} mb={2}>
                <CollapsibleSection
                  title={`${holidayName} (${periodCount} periode${periodCount !== 1 ? 's' : ''})`}
                  warningText={undefined}
                  initializeAsOpen={false}
                >
                  <Box p={2}>
                    <Grid container spacing={2}>
                      {holidays[holidayName].map((_period, index) => (
                        <Grid item xs={12} key={index}>
                          <PeriodCard>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={2}>
                                <LwFormTime
                                  name={`holidays.${holidayName}.${index}.startTime`}
                                  label="Van"
                                  control={control}
                                  disabled={false}
                                />
                              </Grid>

                              <Grid item xs={2}>
                                <LwFormTime
                                  name={`holidays.${holidayName}.${index}.endTime`}
                                  label="Tot"
                                  control={control}
                                  disabled={false}
                                />
                              </Grid>

                              <Grid item xs={3}>
                                <LwFormInput
                                  name={`holidays.${holidayName}.${index}.percentage`}
                                  label="ORT %"
                                  control={control}
                                  inputType="number"
                                />
                              </Grid>
                            </Grid>
                          </PeriodCard>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </CollapsibleSection>
              </Box>
            );
          })}
        </CollapsibleSection>
      </ContentBox>

      <ActionBox mt={4}>
        <Button onClick={onClose} color="inherit">
          Annuleren
        </Button>
        <LwButton onClick={handleSave} color="primary">
          Opslaan
        </LwButton>
      </ActionBox>
    </CompanyOrtFormWrapper>
  );
};

const CompanyOrtFormWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const ContentBox = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  paddingRight: theme.spacing(1),
}));

const PeriodCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
}));

const ActionBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(2),
  paddingTop: theme.spacing(3),
  borderTop: `1px solid ${theme.palette.divider}`,
}));
